import { formatDistanceToNow } from 'date-fns'
import { MessageSquare, MoreVertical, Trash2, Copy, Check, FolderKanban, Pencil, Link2 } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ProjectSelector } from '@/components/projects/ProjectSelector'
import type { ChatbotSession } from '@/types/chatbot'

export interface ChatbotSessionCardProps {
  /** The session data to display */
  session: ChatbotSession
  /** Whether this session is currently selected */
  isSelected?: boolean
  /** Called when the session card is clicked */
  onClick?: () => void
  /** Called when delete is requested */
  onDelete?: () => void
  /** Called when project association is changed */
  onProjectChange?: (projectId: string | null) => void
  /** Additional CSS classes */
  className?: string
}

/**
 * Chat-app style session card — compact, with message preview and relative time.
 */
export function ChatbotSessionCard({
  session,
  isSelected = false,
  onClick,
  onDelete,
  onProjectChange,
  className,
}: ChatbotSessionCardProps) {
  const [copied, setCopied] = useState(false)
  const [projectPopoverOpen, setProjectPopoverOpen] = useState(false)
  const title = session.title ?? 'New Conversation'
  const timeAgo = formatDistanceToNow(new Date(session.updatedAt), { addSuffix: true })

  const handleCopySessionId = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(session.sessionId)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy session ID:', err)
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick?.()
        }
      }}
      className={cn(
        'group relative flex flex-col rounded-xl px-3 py-2.5 text-left text-sm transition-all',
        'hover:bg-accent/60 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isSelected
          ? 'bg-accent shadow-sm border border-accent-foreground/10'
          : 'border border-transparent',
        className
      )}
      aria-selected={isSelected}
      aria-label={`Chat session: ${title}`}
    >
      {/* Row 1: Title + time + menu */}
      <div className="flex items-start gap-1.5">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-[13px] leading-snug line-clamp-1 break-all" title={title}>
            {title}
          </h4>
        </div>
        {/*
          A <time> element, not a span: "2 months ago" is the only form a reader sees, and on its own it
          is not machine-readable and carries no absolute instant. dateTime gives assistive technology and
          anything else parsing the page the real timestamp, and title puts it one hover away.
        */}
        <time
          dateTime={session.updatedAt}
          title={new Date(session.updatedAt).toLocaleString()}
          className="text-[11px] text-muted-foreground/70 whitespace-nowrap pt-0.5 flex-shrink-0"
        >
          {timeAgo}
        </time>
        {onDelete && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="h-5 w-5 flex-shrink-0 flex items-center justify-center rounded hover:bg-accent opacity-0 group-hover:opacity-100 transition-opacity -mr-0.5"
                onClick={(e) => e.stopPropagation()}
                aria-label="Session options"
              >
                <MoreVertical className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={5}>
              <DropdownMenuItem onClick={handleCopySessionId}>
                {copied ? (
                  <Check className="h-4 w-4 mr-2 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4 mr-2" />
                )}
                {copied ? 'Copied!' : 'Copy Session ID'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete()
                }}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Row 2: Message preview + count */}
      <div className="flex items-center gap-2 mt-1">
        <p className="text-xs text-muted-foreground line-clamp-1 flex-1 min-w-0">
          {session.lastMessage || 'No messages yet'}
        </p>
        <div className="flex items-center gap-0.5 text-[11px] text-muted-foreground/60 flex-shrink-0">
          <MessageSquare className="h-3 w-3" />
          <span>{session.messageCount}</span>
        </div>
      </div>

      {/* Row 3: Project badge (compact) */}
      {(session.projectName || onProjectChange) && (
        <div className="flex items-center gap-1 mt-1.5">
          {session.projectName ? (
            <>
              <Link
                to={`/projects/${session.projectId}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Badge
                  variant="secondary"
                  className="text-[10px] gap-0.5 h-5 px-1.5 hover:bg-secondary/80 cursor-pointer"
                >
                  <FolderKanban className="h-2.5 w-2.5" />
                  <span className="line-clamp-1 max-w-[140px]">{session.projectName}</span>
                </Badge>
              </Link>
              {onProjectChange && (
                <Popover open={projectPopoverOpen} onOpenChange={setProjectPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Edit project link"
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-64 p-2"
                    align="start"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ProjectSelector
                      value={session.projectId ?? undefined}
                      onChange={(projectId) => {
                        onProjectChange(projectId ?? null)
                        setProjectPopoverOpen(false)
                      }}
                      allowClear
                      placeholder="Search projects..."
                      className="w-full"
                    />
                  </PopoverContent>
                </Popover>
              )}
            </>
          ) : onProjectChange ? (
            <Popover open={projectPopoverOpen} onOpenChange={setProjectPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 px-1.5 text-[10px] text-muted-foreground/60 hover:text-foreground gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Link2 className="h-2.5 w-2.5" />
                  <span>Link project</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-64 p-2"
                align="start"
                onClick={(e) => e.stopPropagation()}
              >
                <ProjectSelector
                  value={undefined}
                  onChange={(projectId) => {
                    if (projectId) {
                      onProjectChange(projectId)
                      setProjectPopoverOpen(false)
                    }
                  }}
                  placeholder="Search projects..."
                  className="w-full"
                />
              </PopoverContent>
            </Popover>
          ) : null}
        </div>
      )}
    </div>
  )
}
